/**
 * @fileoverview Song search form component.
 * @summary Search form with simple and advanced modes.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/singer/components/SongSearchForm
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch, faSpinner, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import type { FC } from 'react'

interface SongSearchFormProps {
  searchQuery: string
  artistQuery: string
  showAdvancedSearch: boolean
  isSearching: boolean
  isSearchDisabled: boolean
  onSearchQueryChange: (value: string) => void
  onArtistQueryChange: (value: string) => void
  onToggleAdvancedSearch: () => void
  onSubmit: (e: React.FormEvent) => void
}

/**
 * @summary Song search form component.
 * @param {SongSearchFormProps} props - Component props.
 * @returns {ReactElement} Song search form component.
 */
const SongSearchForm: FC<SongSearchFormProps> = ({
  searchQuery,
  artistQuery,
  showAdvancedSearch,
  isSearching,
  isSearchDisabled,
  onSearchQueryChange,
  onArtistQueryChange,
  onToggleAdvancedSearch,
  onSubmit,
}) => {
  const { t } = useTranslation()

  return (
    <div className="card mb-6">
      <h2 className="text-lg font-semibold text-(--color-text-primary) mb-4 flex items-center gap-2">
        <FontAwesomeIcon icon={faSearch} />
        {showAdvancedSearch ? t('singer.advancedSearch') : t('singer.searchSong')}
      </h2>

      <form onSubmit={onSubmit} className="space-y-3">
        {/* Title field */}
        <div className="flex gap-2 items-center">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            placeholder={showAdvancedSearch ? t('singer.titlePlaceholder') : t('singer.searchPlaceholder')}
            className="flex-1 min-w-0 px-4 py-3 text-base border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
          <button
            type="submit"
            className="shrink-0 px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSearching || isSearchDisabled}
          >
            {isSearching ? (
              <FontAwesomeIcon icon={faSpinner} className="animate-spin w-5 h-5" />
            ) : (
              <FontAwesomeIcon icon={faSearch} className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Artist field (advanced search) */}
        {showAdvancedSearch && (
          <div className="flex gap-2 items-center">
            <input
              type="text"
              value={artistQuery}
              onChange={(e) => onArtistQueryChange(e.target.value)}
              placeholder={t('singer.artistPlaceholder')}
              className="flex-1 min-w-0 px-4 py-3 text-base border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
        )}

        {/* Toggle advanced search */}
        <button
          type="button"
          onClick={onToggleAdvancedSearch}
          className="flex items-center gap-2 text-sm text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors"
        >
          <FontAwesomeIcon icon={showAdvancedSearch ? faChevronUp : faChevronDown} className="w-3 h-3" />
          {showAdvancedSearch ? t('singer.simpleSearch') : t('singer.advancedSearchToggle')}
        </button>
      </form>
    </div>
  )
}

export default SongSearchForm

